import { useState } from 'react';
import { calculateShapeCoordinates, getResizeHandle } from '../utils/shapeUtils';

export const useShapeDrawing = () => {
    const [currentShape, setCurrentShape] = useState(null);
    const [selectedTool, setSelectedTool] = useState('pointer');
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [redrawCanvasFunc, setRedrawCanvas] = useState(null);

    const handleDrawStart = (e, rect, position, baseScale, scale) => {
        if (selectedTool === 'pointer') return;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const coords = calculateShapeCoordinates(e, rect, centerX, centerY, position, baseScale, scale);
        
        // Create shape immediately for text tool
        if (selectedTool === 'textbox') {
            return {
                type: 'textbox',
                startX: coords.x,
                startY: coords.y,
                width: 200,
                height: 100,
                content: 'Click the "edit" button in layers to edit',
                color: '#000000',
                lineWidth: 1,
                fontSize: 24
            };
        }
        
        // Handle other shapes...
        if (selectedTool === 'marker') {
            return {
                type: 'marker',
                startX: coords.x,
                startY: coords.y,
                width: 20,  // Fixed size for marker
                height: 20,
                color: '#FF0000',
                lineWidth: 4
            };
        }
        
        setCurrentShape({
            type: selectedTool,
            startX: coords.x,
            startY: coords.y,
            points: [{ x: coords.x, y: coords.y }],
            color: '#FF0000',
            lineWidth: selectedTool === 'freedraw' ? 8 : 8,
            width: 0,
            height: 0
        });
        
        return null;
    };

    const handleDrawMove = (e, rect, position, baseScale, scale) => {
        if (!currentShape || selectedTool === 'pointer') return;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        
        const coords = calculateShapeCoordinates(e, rect, centerX, centerY, position, baseScale, scale);

        switch (selectedTool) {
            case 'freedraw':
                setCurrentShape(prev => ({
                    ...prev,
                    points: [...prev.points, { x: coords.x, y: coords.y }]
                }));
                break;
            case 'line':
            case 'rectangle':
            case 'oval':
                setCurrentShape(prev => ({
                    ...prev,
                    width: coords.x - prev.startX,
                    height: coords.y - prev.startY
                }));
                break;
            default:
                break;
        }
        
        if (redrawCanvasFunc) {
            redrawCanvasFunc();
        }
    };

    const handleDrawEnd = () => {
        if (!currentShape || selectedTool === 'pointer') return;
        
        let newShape = {
            ...currentShape,
            offsetX: 0,
            offsetY: 0
        };

        if (currentShape.type === 'textbox') {
            // Return textbox shape as is
            setCurrentShape(null);
            return newShape;
        }

        // Handle freedraw and other shapes...
        if (currentShape.type === 'freedraw' && currentShape.points.length > 1) {
            const xPoints = currentShape.points.map(p => p.x);
            const yPoints = currentShape.points.map(p => p.y);
            const minX = Math.min(...xPoints);
            const minY = Math.min(...yPoints);
            const maxX = Math.max(...xPoints);
            const maxY = Math.max(...yPoints);
            
            newShape = {
                ...newShape,
                startX: minX,
                startY: minY,
                points: currentShape.points,  // Keep original points
                width: maxX - minX,
                height: maxY - minY
            };
        }
        
        setCurrentShape(null);
        return newShape;
    };

    const handleResizeStart = (e, shape, finalScale, rect, centerX, centerY, position) => {
        const handle = getResizeHandle(e, shape, finalScale, rect, centerX, centerY, position);
        console.log('Resize handle detected:', handle);
        
        if (handle) {
            setDragStart({ x: e.clientX, y: e.clientY });
            
            // Only restrict freedraw shapes to center handle (dragging)
            if (shape.type === 'freedraw' && handle !== 'center') {
                return false;
            }
            
            if (handle === 'center') {
                console.log('Center handle clicked - setting isDragging to true');
                setIsResizing(false);
                setResizeHandle(null);
                setIsDragging(true);
                console.log('State after center handle:', { isResizing: false, isDragging: true });
            } else {
                console.log('Edge/corner handle clicked - setting isResizing to true');
                setIsDragging(false);
                setIsResizing(true);
                setResizeHandle(handle);
                console.log('State after edge handle:', { isResizing: true, isDragging: false, handle });
            }
            return true;
        }
        return false;
    };

    const handleShapeEdit = (e, rect, position, baseScale, scale, editingLayer, updateLayerData) => {
        if (!isResizing && !isDragging) return;

        if (isDragging) {
            const finalScale = baseScale * scale;
            
            // Calculate movement in screen coordinates
            const screenDeltaX = e.clientX - dragStart.x;
            const screenDeltaY = e.clientY - dragStart.y;
            
            // Convert to canvas coordinates considering the current view transformation
            const canvasDeltaX = screenDeltaX / finalScale;
            const canvasDeltaY = screenDeltaY / finalScale;

            updateLayerData(editingLayer, (data) => {
                const newData = { ...data };
                
                if (newData.type === 'freedraw') {
                    newData.points = newData.points.map(point => ({
                        x: point.x + canvasDeltaX,
                        y: point.y + canvasDeltaY
                    }));
                }
                
                newData.startX = data.startX + canvasDeltaX;
                newData.startY = data.startY + canvasDeltaY;
                
                return newData;
            });

            setDragStart({ x: e.clientX, y: e.clientY });
        }

        // Handle resizing
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;
        const mouseX = (e.clientX - rect.left - centerX - position.x) / (baseScale * scale);
        const mouseY = (e.clientY - rect.top - centerY - position.y) / (baseScale * scale);

        updateLayerData(editingLayer, (data) => {
            const newData = data.type === 'textbox' ? {...data} : { ...data };
            
            switch (resizeHandle) {
                case 'corner-br':
                    newData.width = mouseX - newData.startX;
                    newData.height = mouseY - newData.startY;
                    break;
                case 'corner-bl':
                    newData.width = newData.startX + newData.width - mouseX;
                    newData.startX = mouseX;
                    newData.height = mouseY - newData.startY;
                    break;
                case 'corner-tr':
                    newData.width = mouseX - newData.startX;
                    newData.height = newData.startY + newData.height - mouseY;
                    newData.startY = mouseY;
                    break;
                case 'corner-tl':
                    newData.width = newData.startX + newData.width - mouseX;
                    newData.startX = mouseX;
                    newData.height = newData.startY + newData.height - mouseY;
                    newData.startY = mouseY;
                    break;
                case 'edge-h':
                    newData.width = newData.startX + newData.width - mouseX;
                    newData.startX = mouseX;
                    break;
                case 'edge-h-right':
                    newData.width = mouseX - newData.startX;
                    break;
                case 'edge-v':
                    newData.height = newData.startY + newData.height - mouseY;
                    newData.startY = mouseY;
                    break;
                case 'edge-v-bottom':
                    newData.height = mouseY - newData.startY;
                    break;
            }
            
            return newData;  // This preserves all properties
        });

        setDragStart({ x: e.clientX, y: e.clientY });
    };

    const clearDrawingState = () => {
        setCurrentShape(null);
        setIsResizing(false);
        setIsDragging(false);
        setResizeHandle(null);
    };

    return {
        currentShape,
        selectedTool,
        isResizing,
        resizeHandle,
        isDragging,
        dragStart,
        setSelectedTool,
        setDragStart,
        handleDrawStart,
        handleDrawMove,
        handleDrawEnd,
        handleResizeStart,
        clearDrawingState,
        setIsResizing,
        setIsDragging,
        setResizeHandle,
        handleShapeEdit,
        setRedrawCanvas
    };
}; 